import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Pressable,
  FlatList,
  useWindowDimensions,
  ActivityIndicator,
  Image,
  Modal,
  TextInput,
  View as RNView,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { View, Text } from "../components/Themed";
import { useQuery, useMutation, gql } from "@apollo/client";
import { MaterialIcons, Ionicons } from "@expo/vector-icons";
import { v4 as uuidv4 } from "uuid";
import "react-native-get-random-values";
import Config from "../constants/Config";
import { getProductImageUrl } from "../utils/urls";
import i18n from "../utils/localization";
import Alert, { AlertRoot } from "../components/Alert";

// Cash Register uses a dedicated table by default (table #0)
// This can be overridden by selecting another table
const CASH_REGISTER_TABLE_NUMBER = 0;

const PRODUCTS_QUERY = gql`
  query getProducts($token: String!) {
    categories(token: $token) {
      id
      name
      order
    }
    products(token: $token) {
      id
      category
      title
      subtitle
      imageUrls
      prices
      variants
      volumes
      allergens
      published
      available
      options {
        id
        title
        options {
          id
          title
          prices
        }
      }
    }
  }
`;

const TABLES_QUERY = gql`
  query tables($token: String!) {
    tables(token: $token) {
      id
      number
      operator
      available
      finalized
      token
      orders {
        id
      }
    }
  }
`;

const CREATE_ORDER = gql`
  mutation createOrder(
    $id: ID!
    $token: String!
    $operator: ID!
    $table: ID!
    $productIds: [ID!]!
    $quantities: [Int!]!
    $variants: [String!]!
    $capabilities: [String]!
    $comment: String
    $options: [String]!
  ) {
    createOrder(
      id: $id
      token: $token
      operator: $operator
      table: $table
      productIds: $productIds
      quantities: $quantities
      variants: $variants
      comment: $comment
      capabilities: $capabilities
      options: $options
    ) {
      id
      orders {
        id
        productIds
        quantities
        variants
        status
      }
    }
  }
`;

const FINALIZE_TABLE = gql`
  mutation finalizeTable(
    $token: String!
    $operator: ID!
    $table: ID!
    $tableToken: String!
    $paymentType: String!
  ) {
    finalizeTable(
      token: $token
      operator: $operator
      table: $table
      tableToken: $tableToken
      paymentType: $paymentType
    )
  }
`;

const GENERATE_TABLE_TOKEN = gql`
  mutation generateTableToken($token: String!, $operator: ID!, $table: ID!) {
    generateTableToken(token: $token, operator: $operator, table: $table) {
      id
      token
    }
  }
`;

export default function CashRegisterScreen({ navigation }) {
  const window = useWindowDimensions();
  const isWideScreen = window.width > 768;

  const [token, setToken] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]); // { product, variant, quantity, options }
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableModalVisible, setTableModalVisible] = useState(false);
  const [variantModalVisible, setVariantModalVisible] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingVariant, setPendingVariant] = useState(null);
  const [optionsModalVisible, setOptionsModalVisible] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({}); // { optionGroupId: [choiceId1, choiceId2, ...] }
  const [quantityMultiplier, setQuantityMultiplier] = useState(1);
  const [lastAddedProduct, setLastAddedProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const { data, loading, refetch } = useQuery(PRODUCTS_QUERY, {
    variables: { token },
    skip: !token,
  });
  const { data: tablesData, refetch: refetchTables } = useQuery(TABLES_QUERY, {
    variables: { token },
    skip: !token,
  });

  const [createOrder] = useMutation(CREATE_ORDER);
  const [finalizeTable] = useMutation(FINALIZE_TABLE);
  const [generateTableToken] = useMutation(GENERATE_TABLE_TOKEN);

  useEffect(() => {
    async function fetchData() {
      try {
        const storedToken = await AsyncStorage.getItem("token");
        const storedOperatorId = await AsyncStorage.getItem("operatorId");
        setToken(storedToken || "");
        setOperatorId(storedOperatorId || "");
      } catch (ex) {
        console.warn("Failed to load auth from AsyncStorage:", ex);
      }
    }
    fetchData();
  }, []);

  const categories = useMemo(() => {
    if (!data?.categories) return [];
    return [...data.categories].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [data]);

  const products = useMemo(() => {
    if (!data?.products) return [];
    // Show all products - the API may not return unpublished ones anyway
    return data.products;
  }, [data]);

  const filteredProducts = useMemo(() => {
    if (!selectedCategory) return products;
    return products.filter(p => p.category === selectedCategory);
  }, [products, selectedCategory]);

  const tables = useMemo(() => {
    if (!tablesData?.tables) return [];
    return tablesData.tables.filter(t => !t.finalized);
  }, [tablesData]);

  // Find the dedicated cash register table
  const cashRegisterTable = useMemo(() => {
    return tables.find(t => t.number === CASH_REGISTER_TABLE_NUMBER);
  }, [tables]);

  // Auto-select the cash register table when available
  useEffect(() => {
    if (cashRegisterTable && !selectedTable) {
      setSelectedTable(cashRegisterTable);
    }
  }, [cashRegisterTable]);

  // Calculate cart total including options
  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      const variantIndex = item.product.variants.indexOf(item.variant);
      let price = item.product.prices[variantIndex] || 0;

      // Add option prices (selectedOptions is { groupId: [choiceId1, choiceId2, ...] })
      if (item.selectedOptions && item.product.options) {
        Object.entries(item.selectedOptions).forEach(([optionGroupId, choiceIds]) => {
          const optionGroup = item.product.options.find(og => og.id === optionGroupId);
          if (optionGroup) {
            // choiceIds is an array of selected choice IDs
            const choiceIdsArray = Array.isArray(choiceIds) ? choiceIds : [choiceIds];
            choiceIdsArray.forEach(choiceId => {
              const choice = optionGroup.options.find(c => c.id === choiceId);
              if (choice && choice.prices) {
                // Use the price matching the variant index, or first price
                const optionPrice = choice.prices[variantIndex] ?? choice.prices[0] ?? 0;
                price += optionPrice;
              }
            });
          }
        });
      }

      return sum + price * item.quantity;
    }, 0);
  }, [cart]);

  // Don't auto-select category - show all products by default

  // Final function that actually adds to cart with all selections
  const addToCartFinal = useCallback((product, variant, options = {}, qty = null) => {
    const quantityToAdd = qty || quantityMultiplier;

    // Convert options object to flat array of choice IDs for storage
    // options is { groupId: [choiceId1, choiceId2, ...], ... }
    const optionsArray = Object.values(options)
      .flat()
      .filter(Boolean);

    // Track last added for repeat function
    setLastAddedProduct({ product, variant, options });

    // Create a unique key for cart items that includes options
    const optionsKey = [...optionsArray].sort().join(',');

    setCart(prev => {
      const existing = prev.find(
        item => item.product.id === product.id &&
                item.variant === variant &&
                (item.optionsKey || '') === optionsKey
      );
      if (existing) {
        return prev.map(item =>
          item.product.id === product.id &&
          item.variant === variant &&
          (item.optionsKey || '') === optionsKey
            ? { ...item, quantity: item.quantity + quantityToAdd }
            : item
        );
      }
      return [...prev, {
        product,
        variant,
        quantity: quantityToAdd,
        options: optionsArray,
        optionsKey,
        selectedOptions: options // Keep the full selection for display
      }];
    });

    // Reset multiplier after adding
    if (quantityMultiplier > 1) {
      setQuantityMultiplier(1);
    }
  }, [quantityMultiplier]);

  // Show options modal if product has options
  const showOptionsModal = useCallback((product, variant) => {
    if (product.options && product.options.length > 0) {
      setPendingProduct(product);
      setPendingVariant(variant);
      setSelectedOptions({});
      setOptionsModalVisible(true);
    } else {
      // No options, add directly
      addToCartFinal(product, variant);
    }
  }, [addToCartFinal]);

  const addToCart = useCallback((product, variant = null, qty = null, skipOptions = false) => {
    // If product has multiple variants and no variant specified, show picker
    if (product.variants.length > 1 && !variant) {
      setPendingProduct(product);
      setVariantModalVisible(true);
      return;
    }

    const variantToUse = variant || product.variants[0];

    // Check if product has options and we're not skipping
    if (!skipOptions && product.options && product.options.length > 0) {
      showOptionsModal(product, variantToUse);
      return;
    }

    // No options or skipping, add directly
    addToCartFinal(product, variantToUse, {}, qty);
  }, [quantityMultiplier, showOptionsModal, addToCartFinal]);

  const removeFromCart = useCallback((productId, variant, optionsKey = '') => {
    setCart(prev => {
      const existing = prev.find(
        item => item.product.id === productId &&
                item.variant === variant &&
                (item.optionsKey || '') === optionsKey
      );
      if (existing && existing.quantity > 1) {
        return prev.map(item =>
          item.product.id === productId &&
          item.variant === variant &&
          (item.optionsKey || '') === optionsKey
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      }
      return prev.filter(
        item => !(item.product.id === productId &&
                  item.variant === variant &&
                  (item.optionsKey || '') === optionsKey)
      );
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    setLastAddedProduct(null);
  }, []);

  const repeatLast = useCallback(() => {
    if (lastAddedProduct) {
      // Use addToCartFinal to skip options modal and repeat exact item
      addToCartFinal(lastAddedProduct.product, lastAddedProduct.variant, lastAddedProduct.options || {}, 1);
    }
  }, [lastAddedProduct, addToCartFinal]);

  const submitOrder = useCallback(async (paymentType = null) => {
    if (cart.length === 0) {
      Alert.alert("Empty Cart", "Please add items to the cart first.");
      return;
    }

    if (!selectedTable) {
      setTableModalVisible(true);
      return;
    }

    // Guard against double-tap: a second concurrent submit would create duplicate orders
    // and finalize the table twice (double charge / double fiscal receipt).
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      // Create orders for each cart item
      for (const item of cart) {
        const orderId = uuidv4();
        // Backend expects a flat [optionId, choiceId, ...] pair array (even length), not
        // bare choice ids. selectedOptions is { groupId: [choiceId, ...] }.
        const optionPairs = [];
        Object.entries(item.selectedOptions || {}).forEach(([groupId, choiceIds]) => {
          (Array.isArray(choiceIds) ? choiceIds : [choiceIds]).filter(Boolean).forEach((choiceId) => {
            optionPairs.push(groupId, choiceId);
          });
        });
        await createOrder({
          variables: {
            id: orderId,
            token,
            operator: operatorId,
            table: selectedTable.id,
            productIds: [item.product.id],
            quantities: [item.quantity],
            variants: [item.variant],
            capabilities: [],
            comment: null,
            options: optionPairs,
          },
        });
      }

      // If payment type specified, finalize immediately
      if (paymentType) {
        // First generate table token if needed
        let tableToken = selectedTable.token;
        if (!tableToken) {
          const tokenResult = await generateTableToken({
            variables: {
              token,
              operator: operatorId,
              table: selectedTable.id,
            },
          });
          tableToken = tokenResult.data?.generateTableToken?.token || "";
        }

        await finalizeTable({
          variables: {
            token,
            operator: operatorId,
            table: selectedTable.id,
            tableToken: tableToken,
            paymentType,
          },
        });
      }

      clearCart();
      setSelectedTable(null);
      refetchTables();

      Alert.alert(
        "Success",
        paymentType
          ? `Order completed with ${paymentType} payment!`
          : "Order submitted successfully!"
      );
    } catch (err) {
      console.error("Order error:", err);
      Alert.alert("Error", err.message || "Failed to submit order");
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [cart, selectedTable, token, operatorId, createOrder, finalizeTable, generateTableToken, clearCart, refetchTables]);

  // Calculate number of columns based on screen width
  const numColumns = useMemo(() => {
    if (window.width > 1400) return 6;
    if (window.width > 1100) return 5;
    if (window.width > 900) return 4;
    if (window.width > 600) return 3;
    return 2;
  }, [window.width]);

  const renderProductTile = useCallback(({ item }) => {
    const imageUrl = item.imageUrls?.[0] ? getProductImageUrl(item.imageUrls[0]) : null;
    // A product with an empty prices[] is valid in the schema; coerce so price.toFixed()
    // can't crash the whole product grid.
    const price = Number(item.prices?.[0]) || 0;
    const hasMultipleVariants = (item.variants?.length || 0) > 1;

    // Check if item is in cart (sum all variants)
    const cartItems = cart.filter(c => c.product.id === item.id);
    const inCart = cartItems.length > 0;
    const cartQty = cartItems.reduce((sum, c) => sum + c.quantity, 0);
    // Get the first cart item for single variant products
    const singleCartItem = cartItems[0];

    return (
      <Pressable
        style={[styles.productTile, inCart && styles.productTileInCart]}
        onPress={() => addToCart(item)}
      >
        {imageUrl ? (
          <Image source={{ uri: imageUrl, cache: 'default' }} style={styles.productImage} />
        ) : (
          <RNView style={styles.productImagePlaceholder}>
            <MaterialIcons name="fastfood" size={32} color="#ccc" />
          </RNView>
        )}
        <RNView style={styles.productInfo}>
          <Text style={styles.productTitle} numberOfLines={2}>
            {i18n.t(item.title)}
          </Text>
          <Text style={styles.productPrice}>
            {price.toFixed(2)}
            {hasMultipleVariants && "+"}
          </Text>
        </RNView>

        {/* Quick quantity controls on tile when in cart */}
        {inCart && !hasMultipleVariants && singleCartItem && (
          <RNView style={styles.tileQtyControls}>
            <Pressable
              style={styles.tileQtyBtn}
              onPress={(e) => {
                e.stopPropagation();
                removeFromCart(item.id, singleCartItem.variant);
              }}
            >
              <MaterialIcons name="remove" size={18} color="#fff" />
            </Pressable>
            <Text style={styles.tileQtyText}>{cartQty}</Text>
            <Pressable
              style={styles.tileQtyBtn}
              onPress={(e) => {
                e.stopPropagation();
                addToCart(item, singleCartItem.variant, 1);
              }}
            >
              <MaterialIcons name="add" size={18} color="#fff" />
            </Pressable>
          </RNView>
        )}

        {/* Badge for multi-variant products */}
        {inCart && hasMultipleVariants && (
          <RNView style={styles.cartBadge}>
            <Text style={styles.cartBadgeText}>{cartQty}</Text>
          </RNView>
        )}

        {/* Multiplier indicator */}
        {quantityMultiplier > 1 && (
          <RNView style={styles.multiplierBadge}>
            <Text style={styles.multiplierBadgeText}>x{quantityMultiplier}</Text>
          </RNView>
        )}
      </Pressable>
    );
  }, [cart, addToCart, removeFromCart, quantityMultiplier]);

  // Helper to get option choice names from product and selectedOptions
  const getOptionChoiceNames = useCallback((product, selectedOptions) => {
    if (!selectedOptions || !product.options) return [];
    const names = [];
    Object.entries(selectedOptions).forEach(([optionGroupId, choiceIds]) => {
      const optionGroup = product.options.find(og => og.id === optionGroupId);
      if (optionGroup) {
        // choiceIds can be array or single value (for backwards compatibility)
        const choiceIdsArray = Array.isArray(choiceIds) ? choiceIds : [choiceIds];
        choiceIdsArray.forEach(choiceId => {
          const choice = optionGroup.options.find(c => c.id === choiceId);
          if (choice) names.push(choice.title);
        });
      }
    });
    return names;
  }, []);

  const renderCartItem = useCallback(({ item, index }) => {
    const variantIndex = item.product.variants.indexOf(item.variant);
    let unitPrice = item.product.prices[variantIndex] || 0;

    // Add option prices to unit price (selectedOptions is { groupId: [choiceId1, ...] })
    if (item.selectedOptions && item.product.options) {
      Object.entries(item.selectedOptions).forEach(([optionGroupId, choiceIds]) => {
        const optionGroup = item.product.options.find(og => og.id === optionGroupId);
        if (optionGroup) {
          const choiceIdsArray = Array.isArray(choiceIds) ? choiceIds : [choiceIds];
          choiceIdsArray.forEach(choiceId => {
            const choice = optionGroup.options.find(c => c.id === choiceId);
            if (choice && choice.prices) {
              const optionPrice = choice.prices[variantIndex] ?? choice.prices[0] ?? 0;
              unitPrice += optionPrice;
            }
          });
        }
      });
    }

    const subtotal = unitPrice * item.quantity;
    const optionNames = getOptionChoiceNames(item.product, item.selectedOptions);

    return (
      <View style={styles.cartItem}>
        <View style={styles.cartItemInfo}>
          <Text style={styles.cartItemTitle} numberOfLines={1}>
            {i18n.t(item.product.title)}
          </Text>
          {item.product.variants.length > 1 && (
            <Text style={styles.cartItemVariant}>{item.variant}</Text>
          )}
          {optionNames.length > 0 && (
            <Text style={styles.cartItemOptions} numberOfLines={2}>
              + {optionNames.map(n => i18n.t(n)).join(', ')}
            </Text>
          )}
        </View>
        <View style={styles.cartItemControls}>
          <TouchableOpacity
            style={styles.qtyButton}
            onPress={() => removeFromCart(item.product.id, item.variant, item.optionsKey)}
          >
            <MaterialIcons name="remove" size={20} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.qtyText}>{item.quantity}</Text>
          <TouchableOpacity
            style={styles.qtyButton}
            onPress={() => addToCartFinal(item.product, item.variant, item.selectedOptions || {}, 1)}
          >
            <MaterialIcons name="add" size={20} color="#fff" />
          </TouchableOpacity>
        </View>
        <Text style={styles.cartItemPrice}>{subtotal.toFixed(2)}</Text>
      </View>
    );
  }, [addToCartFinal, removeFromCart, getOptionChoiceNames]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Config.companyColor || "#007AFF"} />
      </View>
    );
  }

  if (!products || products.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <MaterialIcons name="inventory" size={48} color="#ccc" />
        <Text style={{ marginTop: 16, color: "#666" }}>No products available</Text>
        <Text style={{ marginTop: 8, color: "#999", fontSize: 12 }}>
          {data ? `Data loaded, ${data.products?.length || 0} products found` : 'No data received'}
        </Text>
      </View>
    );
  }

  const quickActionBar = useMemo(() => (
    <View style={styles.quickActionBar}>
      {/* Quantity Multipliers */}
      <View style={styles.multiplierSection}>
        <Text style={styles.multiplierLabel}>Qty:</Text>
        {[1, 2, 5, 10].map(num => (
          <TouchableOpacity
            key={num}
            style={[
              styles.multiplierBtn,
              quantityMultiplier === num && styles.multiplierBtnActive,
            ]}
            onPress={() => setQuantityMultiplier(num)}
          >
            <Text style={[
              styles.multiplierBtnText,
              quantityMultiplier === num && styles.multiplierBtnTextActive,
            ]}>
              {num === 1 ? "x1" : `x${num}`}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Repeat Last Button */}
      <TouchableOpacity
        style={[
          styles.repeatBtn,
          !lastAddedProduct && styles.repeatBtnDisabled,
        ]}
        onPress={repeatLast}
        disabled={!lastAddedProduct}
      >
        <MaterialIcons
          name="replay"
          size={20}
          color={lastAddedProduct ? "#007AFF" : "#ccc"}
        />
        <Text style={[
          styles.repeatBtnText,
          !lastAddedProduct && styles.repeatBtnTextDisabled,
        ]}>
          Repeat
        </Text>
      </TouchableOpacity>
    </View>
  ), [quantityMultiplier, lastAddedProduct, repeatLast]);

  const categoryTabs = useMemo(() => (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.categoryTabs}
      contentContainerStyle={styles.categoryTabsContent}
    >
      <TouchableOpacity
        style={[
          styles.categoryTab,
          !selectedCategory && styles.categoryTabActive,
        ]}
        onPress={() => setSelectedCategory(null)}
      >
        <Text style={[
          styles.categoryTabText,
          !selectedCategory && styles.categoryTabTextActive,
        ]}>
          All
        </Text>
      </TouchableOpacity>
      {categories.map(cat => (
        <TouchableOpacity
          key={cat.id}
          style={[
            styles.categoryTab,
            selectedCategory === cat.id && styles.categoryTabActive,
          ]}
          onPress={() => setSelectedCategory(cat.id)}
        >
          <Text style={[
            styles.categoryTabText,
            selectedCategory === cat.id && styles.categoryTabTextActive,
          ]}>
            {i18n.t(cat.name)}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  ), [categories, selectedCategory]);

  const isCashRegisterDefault = selectedTable?.number === CASH_REGISTER_TABLE_NUMBER;

  const CartPanel = () => (
    <View style={[styles.cartPanel, !isWideScreen && styles.cartPanelMobile]}>
      <View style={styles.cartHeader}>
        <Text style={styles.cartTitle}>Current Order</Text>
        {selectedTable && (
          <TouchableOpacity
            style={[
              styles.tableSelector,
              isCashRegisterDefault && styles.tableSelectorDefault,
            ]}
            onPress={() => setTableModalVisible(true)}
          >
            <MaterialIcons
              name={isCashRegisterDefault ? "point-of-sale" : "table-restaurant"}
              size={18}
              color={isCashRegisterDefault ? "#4CAF50" : "#007AFF"}
            />
            <Text style={[
              styles.tableSelectorText,
              isCashRegisterDefault && styles.tableSelectorTextDefault,
            ]}>
              {isCashRegisterDefault ? "POS" : `#${selectedTable.number}`}
            </Text>
          </TouchableOpacity>
        )}
        {!selectedTable && (
          <TouchableOpacity
            style={[styles.tableSelector, styles.tableSelectorWarning]}
            onPress={() => setTableModalVisible(true)}
          >
            <MaterialIcons name="warning" size={18} color="#ff9500" />
            <Text style={styles.tableSelectorTextWarning}>No Table</Text>
          </TouchableOpacity>
        )}
      </View>

      {cart.length === 0 ? (
        <View style={styles.emptyCart}>
          <MaterialIcons name="shopping-cart" size={48} color="#ddd" />
          <Text style={styles.emptyCartText}>Cart is empty</Text>
        </View>
      ) : (
        <>
          <FlatList
            data={cart}
            renderItem={renderCartItem}
            keyExtractor={(item, index) => `${item.product.id}-${item.variant}-${index}`}
            style={styles.cartList}
          />
          <View style={styles.cartFooter}>
            <View style={styles.cartTotal}>
              <Text style={styles.cartTotalLabel}>Total</Text>
              <Text style={styles.cartTotalValue}>{cartTotal.toFixed(2)}</Text>
            </View>

            <TouchableOpacity
              style={styles.clearButton}
              onPress={clearCart}
            >
              <MaterialIcons name="delete-outline" size={20} color="#ff3b30" />
              <Text style={styles.clearButtonText}>Clear</Text>
            </TouchableOpacity>

            <View style={styles.paymentButtons}>
              <TouchableOpacity
                style={[styles.payButton, styles.payButtonCash, isSubmitting && styles.payButtonDisabled]}
                onPress={() => submitOrder("cash")}
                disabled={isSubmitting}
              >
                <MaterialIcons name="attach-money" size={22} color="#fff" />
                <Text style={styles.payButtonText}>Cash</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.payButton, styles.payButtonCard, isSubmitting && styles.payButtonDisabled]}
                onPress={() => submitOrder("card")}
                disabled={isSubmitting}
              >
                <MaterialIcons name="credit-card" size={22} color="#fff" />
                <Text style={styles.payButtonText}>Card</Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={[styles.holdButton, isSubmitting && styles.payButtonDisabled]}
              onPress={() => submitOrder(null)}
              disabled={isSubmitting}
            >
              <MaterialIcons name="pause-circle-outline" size={20} color="#007AFF" />
              <Text style={styles.holdButtonText}>Hold Order (No Payment)</Text>
            </TouchableOpacity>
          </View>
        </>
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={[styles.mainContent, isWideScreen && styles.mainContentWide]}>
        {quickActionBar}
        {categoryTabs}
        <FlatList
          data={filteredProducts}
          renderItem={renderProductTile}
          keyExtractor={item => item.id}
          numColumns={numColumns}
          key={numColumns}
          contentContainerStyle={styles.productGrid}
          columnWrapperStyle={styles.productRow}
        />
      </View>

      <CartPanel />

      {/* Table Selection Modal */}
      <Modal
        visible={tableModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setTableModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Table</Text>
              <TouchableOpacity onPress={() => setTableModalVisible(false)}>
                <MaterialIcons name="close" size={24} color="#333" />
              </TouchableOpacity>
            </View>

            {/* Cash Register Default Option */}
            {cashRegisterTable && (
              <View style={styles.defaultTableSection}>
                <TouchableOpacity
                  style={[
                    styles.defaultTableItem,
                    selectedTable?.id === cashRegisterTable.id && styles.defaultTableItemSelected,
                  ]}
                  onPress={() => {
                    setSelectedTable(cashRegisterTable);
                    setTableModalVisible(false);
                  }}
                >
                  <MaterialIcons
                    name="point-of-sale"
                    size={24}
                    color={selectedTable?.id === cashRegisterTable.id ? "#fff" : "#4CAF50"}
                  />
                  <View style={styles.defaultTableInfo}>
                    <Text style={[
                      styles.defaultTableTitle,
                      selectedTable?.id === cashRegisterTable.id && styles.defaultTableTitleSelected,
                    ]}>
                      Cash Register (Default)
                    </Text>
                    <Text style={[
                      styles.defaultTableSubtitle,
                      selectedTable?.id === cashRegisterTable.id && styles.defaultTableSubtitleSelected,
                    ]}>
                      Table #{cashRegisterTable.number} - Quick checkout
                    </Text>
                  </View>
                  {cashRegisterTable.orders?.length > 0 && (
                    <View style={styles.tableOrdersBadge}>
                      <Text style={styles.tableOrdersBadgeText}>
                        {cashRegisterTable.orders.length}
                      </Text>
                    </View>
                  )}
                </TouchableOpacity>
              </View>
            )}

            {!cashRegisterTable && (
              <View style={styles.noDefaultTableWarning}>
                <MaterialIcons name="info-outline" size={20} color="#ff9500" />
                <Text style={styles.noDefaultTableText}>
                  No dedicated POS table (#{CASH_REGISTER_TABLE_NUMBER}) found. Please select a table or create table #{CASH_REGISTER_TABLE_NUMBER}.
                </Text>
              </View>
            )}

            <Text style={styles.tableSectionTitle}>Other Tables</Text>
            <ScrollView style={styles.tableList}>
              {tables.filter(t => t.number !== CASH_REGISTER_TABLE_NUMBER).map(table => (
                <TouchableOpacity
                  key={table.id}
                  style={[
                    styles.tableItem,
                    selectedTable?.id === table.id && styles.tableItemSelected,
                  ]}
                  onPress={() => {
                    setSelectedTable(table);
                    setTableModalVisible(false);
                  }}
                >
                  <MaterialIcons
                    name="table-restaurant"
                    size={24}
                    color={selectedTable?.id === table.id ? "#fff" : "#007AFF"}
                  />
                  <Text style={[
                    styles.tableItemText,
                    selectedTable?.id === table.id && styles.tableItemTextSelected,
                  ]}>
                    Table #{table.number}
                  </Text>
                  {table.orders?.length > 0 && (
                    <View style={styles.tableOrdersBadge}>
                      <Text style={styles.tableOrdersBadgeText}>
                        {table.orders.length}
                      </Text>
                    </View>
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Variant Selection Modal */}
      <Modal
        visible={variantModalVisible}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setVariantModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, styles.variantModal]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {pendingProduct && i18n.t(pendingProduct.title)}
              </Text>
              <TouchableOpacity onPress={() => setVariantModalVisible(false)}>
                <MaterialIcons name="close" size={24} color="#333" />
              </TouchableOpacity>
            </View>
            <View style={styles.variantList}>
              {pendingProduct?.variants.map((variant, index) => (
                <TouchableOpacity
                  key={variant}
                  style={styles.variantItem}
                  onPress={() => {
                    setVariantModalVisible(false);
                    // Chain to options modal if product has options
                    if (pendingProduct.options && pendingProduct.options.length > 0) {
                      showOptionsModal(pendingProduct, variant);
                    } else {
                      addToCartFinal(pendingProduct, variant);
                      setPendingProduct(null);
                    }
                  }}
                >
                  <Text style={styles.variantItemText}>{variant}</Text>
                  <Text style={styles.variantItemPrice}>
                    {pendingProduct.prices[index]?.toFixed(2)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      </Modal>

      {/* Options Selection Modal */}
      <Modal
        visible={optionsModalVisible}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setOptionsModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, styles.optionsModal]}>
            <View style={styles.modalHeader}>
              <View style={{ flex: 1 }}>
                <Text style={styles.modalTitle}>
                  {pendingProduct && i18n.t(pendingProduct.title)}
                </Text>
                {pendingVariant && pendingProduct?.variants.length > 1 && (
                  <Text style={styles.modalSubtitle}>{pendingVariant}</Text>
                )}
              </View>
              <TouchableOpacity onPress={() => {
                setOptionsModalVisible(false);
                setPendingProduct(null);
                setPendingVariant(null);
                setSelectedOptions({});
              }}>
                <MaterialIcons name="close" size={24} color="#333" />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.optionsList}>
              {pendingProduct?.options?.map((optionGroup) => {
                const variantIndex = pendingProduct.variants.indexOf(pendingVariant);
                return (
                  <View key={optionGroup.id} style={styles.optionGroup}>
                    <Text style={styles.optionGroupTitle}>{i18n.t(optionGroup.title)}</Text>
                    {optionGroup.options.map((choice) => {
                      const groupChoices = selectedOptions[optionGroup.id] || [];
                      const isSelected = groupChoices.includes(choice.id);
                      const choicePrice = choice.prices?.[variantIndex] ?? choice.prices?.[0] ?? 0;
                      return (
                        <TouchableOpacity
                          key={choice.id}
                          style={[
                            styles.optionChoice,
                            isSelected && styles.optionChoiceSelected,
                          ]}
                          onPress={() => {
                            setSelectedOptions(prev => {
                              const currentChoices = prev[optionGroup.id] || [];
                              const isCurrentlySelected = currentChoices.includes(choice.id);

                              if (isCurrentlySelected) {
                                // Remove from selection
                                const newChoices = currentChoices.filter(id => id !== choice.id);
                                if (newChoices.length === 0) {
                                  const next = { ...prev };
                                  delete next[optionGroup.id];
                                  return next;
                                }
                                return { ...prev, [optionGroup.id]: newChoices };
                              } else {
                                // Add to selection
                                return { ...prev, [optionGroup.id]: [...currentChoices, choice.id] };
                              }
                            });
                          }}
                        >
                          <View style={styles.optionChoiceInfo}>
                            <View style={[
                              styles.optionCheckbox,
                              isSelected && styles.optionCheckboxSelected,
                            ]}>
                              {isSelected && (
                                <MaterialIcons name="check" size={16} color="#fff" />
                              )}
                            </View>
                            <Text style={[
                              styles.optionChoiceText,
                              isSelected && styles.optionChoiceTextSelected,
                            ]}>
                              {i18n.t(choice.title)}
                            </Text>
                          </View>
                          {choicePrice > 0 && (
                            <Text style={[
                              styles.optionChoicePrice,
                              isSelected && styles.optionChoicePriceSelected,
                            ]}>
                              +{choicePrice.toFixed(2)}
                            </Text>
                          )}
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                );
              })}
            </ScrollView>
            <View style={styles.optionsFooter}>
              <TouchableOpacity
                style={styles.skipOptionsBtn}
                onPress={() => {
                  addToCartFinal(pendingProduct, pendingVariant, {});
                  setOptionsModalVisible(false);
                  setPendingProduct(null);
                  setPendingVariant(null);
                  setSelectedOptions({});
                }}
              >
                <Text style={styles.skipOptionsBtnText}>Skip Options</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.addWithOptionsBtn}
                onPress={() => {
                  addToCartFinal(pendingProduct, pendingVariant, selectedOptions);
                  setOptionsModalVisible(false);
                  setPendingProduct(null);
                  setPendingVariant(null);
                  setSelectedOptions({});
                }}
              >
                <MaterialIcons name="add-shopping-cart" size={20} color="#fff" />
                <Text style={styles.addWithOptionsBtnText}>Add to Cart</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <AlertRoot />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: "#f5f5f5",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  mainContent: {
    flex: 1,
  },
  mainContentWide: {
    flex: 2,
  },
  categoryTabs: {
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    minHeight: 56,
    maxHeight: 56,
  },
  categoryTabsContent: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    alignItems: "center",
  },
  categoryTab: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    marginHorizontal: 4,
    borderRadius: 20,
    backgroundColor: "#f0f0f0",
  },
  categoryTabActive: {
    backgroundColor: "#007AFF",
  },
  categoryTabText: {
    fontSize: 15,
    fontWeight: "600",
    color: "#666",
  },
  categoryTabTextActive: {
    color: "#fff",
  },
  productGrid: {
    padding: 8,
  },
  productRow: {
    justifyContent: "flex-start",
  },
  productTile: {
    flex: 1,
    margin: 4,
    backgroundColor: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    maxWidth: 180,
    minWidth: 120,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  productTileInCart: {
    borderWidth: 2,
    borderColor: "#007AFF",
  },
  productTileContent: {
    flex: 1,
  },
  productImage: {
    width: "100%",
    height: 80,
    resizeMode: "cover",
  },
  productImagePlaceholder: {
    width: "100%",
    height: 80,
    backgroundColor: "#f8f8f8",
    justifyContent: "center",
    alignItems: "center",
  },
  productInfo: {
    padding: 8,
  },
  productTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: "#333",
    marginBottom: 4,
  },
  productPrice: {
    fontSize: 14,
    fontWeight: "700",
    color: "#007AFF",
  },
  cartBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    backgroundColor: "#007AFF",
    borderRadius: 12,
    minWidth: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  cartBadgeText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "bold",
  },
  tileQtyControls: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#007AFF",
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tileQtyBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "rgba(255,255,255,0.3)",
    justifyContent: "center",
    alignItems: "center",
  },
  tileQtyText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "bold",
    marginHorizontal: 12,
    minWidth: 24,
    textAlign: "center",
  },
  multiplierBadge: {
    position: "absolute",
    top: 8,
    left: 8,
    backgroundColor: "#ff9500",
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  multiplierBadgeText: {
    color: "#fff",
    fontSize: 11,
    fontWeight: "bold",
  },
  quickActionBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#fff",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  multiplierSection: {
    flexDirection: "row",
    alignItems: "center",
  },
  multiplierLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#666",
    marginRight: 8,
  },
  multiplierBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    marginHorizontal: 3,
    borderRadius: 8,
    backgroundColor: "#f0f0f0",
  },
  multiplierBtnActive: {
    backgroundColor: "#007AFF",
  },
  multiplierBtnText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#666",
  },
  multiplierBtnTextActive: {
    color: "#fff",
  },
  repeatBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#e3f2fd",
  },
  repeatBtnDisabled: {
    backgroundColor: "#f5f5f5",
  },
  repeatBtnText: {
    marginLeft: 6,
    fontSize: 14,
    fontWeight: "600",
    color: "#007AFF",
  },
  repeatBtnTextDisabled: {
    color: "#ccc",
  },
  cartPanel: {
    width: 320,
    backgroundColor: "#fff",
    borderLeftWidth: 1,
    borderLeftColor: "#e0e0e0",
  },
  cartPanelMobile: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    width: "100%",
    maxHeight: "50%",
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
    borderLeftWidth: 0,
  },
  cartHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  cartTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#333",
  },
  tableSelector: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: "#e3f2fd",
    borderRadius: 16,
  },
  tableSelectorText: {
    marginLeft: 4,
    fontSize: 14,
    fontWeight: "600",
    color: "#007AFF",
  },
  tableSelectorDefault: {
    backgroundColor: "#e8f5e9",
  },
  tableSelectorTextDefault: {
    color: "#4CAF50",
  },
  tableSelectorWarning: {
    backgroundColor: "#fff3e0",
  },
  tableSelectorTextWarning: {
    marginLeft: 4,
    fontSize: 14,
    fontWeight: "600",
    color: "#ff9500",
  },
  emptyCart: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  emptyCartText: {
    marginTop: 12,
    fontSize: 14,
    color: "#999",
  },
  cartList: {
    flex: 1,
  },
  cartItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  cartItemInfo: {
    flex: 1,
  },
  cartItemTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#333",
  },
  cartItemVariant: {
    fontSize: 12,
    color: "#888",
    marginTop: 2,
  },
  cartItemControls: {
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: 8,
  },
  qtyButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#007AFF",
    justifyContent: "center",
    alignItems: "center",
  },
  qtyText: {
    marginHorizontal: 12,
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
    minWidth: 24,
    textAlign: "center",
  },
  cartItemPrice: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    minWidth: 50,
    textAlign: "right",
  },
  cartFooter: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
  },
  cartTotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  cartTotalLabel: {
    fontSize: 18,
    fontWeight: "600",
    color: "#333",
  },
  cartTotalValue: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#007AFF",
  },
  clearButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 8,
    marginBottom: 12,
  },
  clearButtonText: {
    marginLeft: 4,
    fontSize: 14,
    color: "#ff3b30",
  },
  paymentButtons: {
    flexDirection: "row",
    marginBottom: 12,
  },
  payButtonDisabled: {
    opacity: 0.5,
  },
  payButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 14,
    borderRadius: 8,
    marginHorizontal: 4,
  },
  payButtonCash: {
    backgroundColor: "#34c759",
  },
  payButtonCard: {
    backgroundColor: "#007AFF",
  },
  payButtonText: {
    marginLeft: 6,
    fontSize: 16,
    fontWeight: "bold",
    color: "#fff",
  },
  holdButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
    borderWidth: 1,
    borderColor: "#007AFF",
    borderRadius: 8,
  },
  holdButtonText: {
    marginLeft: 6,
    fontSize: 14,
    color: "#007AFF",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#fff",
    borderRadius: 16,
    width: "90%",
    maxWidth: 400,
    maxHeight: "80%",
    overflow: "hidden",
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#333",
  },
  tableList: {
    padding: 16,
    maxHeight: 300,
  },
  tableSectionTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#666",
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 4,
  },
  defaultTableSection: {
    padding: 16,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  defaultTableItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderRadius: 12,
    backgroundColor: "#e8f5e9",
    borderWidth: 2,
    borderColor: "#4CAF50",
  },
  defaultTableItemSelected: {
    backgroundColor: "#4CAF50",
  },
  defaultTableInfo: {
    flex: 1,
    marginLeft: 12,
  },
  defaultTableTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
  },
  defaultTableTitleSelected: {
    color: "#fff",
  },
  defaultTableSubtitle: {
    fontSize: 12,
    color: "#666",
    marginTop: 2,
  },
  defaultTableSubtitleSelected: {
    color: "rgba(255,255,255,0.8)",
  },
  noDefaultTableWarning: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    backgroundColor: "#fff3e0",
    margin: 16,
    marginBottom: 8,
    borderRadius: 8,
  },
  noDefaultTableText: {
    flex: 1,
    marginLeft: 8,
    fontSize: 13,
    color: "#e65100",
    lineHeight: 18,
  },
  tableItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderRadius: 12,
    backgroundColor: "#f8f8f8",
    marginBottom: 8,
  },
  tableItemSelected: {
    backgroundColor: "#007AFF",
  },
  tableItemText: {
    marginLeft: 12,
    fontSize: 16,
    fontWeight: "500",
    color: "#333",
  },
  tableItemTextSelected: {
    color: "#fff",
  },
  tableOrdersBadge: {
    marginLeft: "auto",
    backgroundColor: "#ff9500",
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 6,
  },
  tableOrdersBadgeText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "bold",
  },
  variantModal: {
    maxWidth: 300,
  },
  variantList: {
    padding: 16,
  },
  variantItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderRadius: 12,
    backgroundColor: "#f8f8f8",
    marginBottom: 8,
  },
  variantItemText: {
    fontSize: 16,
    fontWeight: "500",
    color: "#333",
  },
  variantItemPrice: {
    fontSize: 16,
    fontWeight: "600",
    color: "#007AFF",
  },
  // Cart item options
  cartItemOptions: {
    fontSize: 11,
    color: "#007AFF",
    marginTop: 2,
    fontStyle: "italic",
  },
  // Options modal
  optionsModal: {
    maxWidth: 400,
  },
  modalSubtitle: {
    fontSize: 14,
    color: "#666",
    marginTop: 2,
  },
  optionsList: {
    padding: 16,
    maxHeight: 400,
  },
  optionGroup: {
    marginBottom: 20,
  },
  optionGroupTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
    marginBottom: 10,
  },
  optionChoice: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    borderRadius: 10,
    backgroundColor: "#f8f8f8",
    marginBottom: 6,
  },
  optionChoiceSelected: {
    backgroundColor: "#e3f2fd",
    borderWidth: 1,
    borderColor: "#007AFF",
  },
  optionChoiceInfo: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  optionCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#ccc",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 10,
  },
  optionCheckboxSelected: {
    backgroundColor: "#007AFF",
    borderColor: "#007AFF",
  },
  optionChoiceText: {
    fontSize: 15,
    color: "#333",
    flex: 1,
  },
  optionChoiceTextSelected: {
    fontWeight: "500",
    color: "#007AFF",
  },
  optionChoicePrice: {
    fontSize: 14,
    fontWeight: "500",
    color: "#666",
    marginLeft: 8,
  },
  optionChoicePriceSelected: {
    color: "#007AFF",
  },
  optionsFooter: {
    flexDirection: "row",
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: "#f0f0f0",
  },
  skipOptionsBtn: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#ddd",
    alignItems: "center",
    marginRight: 8,
  },
  skipOptionsBtnText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#666",
  },
  addWithOptionsBtn: {
    flex: 2,
    flexDirection: "row",
    padding: 14,
    borderRadius: 8,
    backgroundColor: "#007AFF",
    alignItems: "center",
    justifyContent: "center",
  },
  addWithOptionsBtnText: {
    marginLeft: 6,
    fontSize: 14,
    fontWeight: "bold",
    color: "#fff",
  },
});
